import type { Site } from "../../types/scene";
import { logger } from "../../utils/logger";
import { formatMjcfNumber } from "./xmlGenerationHelpers";
import type { ExportLight } from "./xmlGenerationTypes";

type LightXmlHelpers = {
  arrayToString: (values: Array<number | string>) => string;
  findSiteByIdentifier: (identifier: string | undefined | null) => Site | null;
  normalizeQuat: (values: unknown) => number[];
  quatMultiply: (a: number[], b: number[]) => number[];
  quatRotateVec: (qRaw: number[], vRaw: number[]) => number[];
  sanitizeMjcfName: (value: string) => string;
  viewerVecToMujoco: (vRaw: number[]) => number[];
  xmlEscapeAttr: (value: string) => string;
};

const RESERVED_LIGHT_ATTRS = new Set([
  "name",
  "class",
  "pos",
  "dir",
  "type",
  "directional",
  "active",
  "castshadow",
  "diffuse",
  "ambient",
  "specular",
  "range",
  "cutoff",
  "exponent",
  "attenuation",
  "intensity",
  "bulbradius",
  "mode",
  "target",
]);

const LIGHT_ATTR_ORDER = ["texture", "range", "cutoff", "exponent", "attenuation"];

const safeVec3 = (value: unknown, fallback: number[]): number[] => {
  if (!Array.isArray(value) || value.length < 3) return [...fallback];
  return [0, 1, 2].map((index) => {
    const numeric = Number(value[index]);
    return Number.isFinite(numeric) ? numeric : fallback[index];
  });
};

const appendLightAttrs = ({
  line,
  light,
  bodyUuidToXmlName,
  helpers,
}: {
  line: string;
  light: ExportLight;
  bodyUuidToXmlName: Record<string, string>;
  helpers: LightXmlHelpers;
}): string => {
  const { arrayToString, xmlEscapeAttr } = helpers;
  const type = String(light?.type || "spot").trim() || "spot";
  line += ` type="${xmlEscapeAttr(type)}"`;
  line += ` active="${light.active === false ? "false" : "true"}"`;
  line += ` castshadow="${light.castshadow === false ? "false" : "true"}"`;
  line += ` diffuse="${arrayToString(safeVec3(light.diffuse, [0.7, 0.7, 0.7]))}"`;
  line += ` ambient="${arrayToString(safeVec3(light.ambient, [0, 0, 0]))}"`;
  line += ` specular="${arrayToString(safeVec3(light.specular, [0.3, 0.3, 0.3]))}"`;

  const range = Number(light.range);
  if (Number.isFinite(range)) line += ` range="${formatMjcfNumber(Math.max(0, range))}"`;
  const cutoff = Number(light.cutoff);
  if (Number.isFinite(cutoff)) {
    line += ` cutoff="${formatMjcfNumber(Math.max(0, Math.min(180, cutoff)))}"`;
  }
  const exponent = Number(light.exponent);
  if (Number.isFinite(exponent)) line += ` exponent="${formatMjcfNumber(Math.max(0, exponent))}"`;
  line += ` attenuation="${arrayToString(safeVec3(light.attenuation, [1, 0, 0]))}"`;

  const intensity = Number(light.intensity);
  if (Number.isFinite(intensity)) line += ` intensity="${formatMjcfNumber(intensity)}"`;
  const bulbradius = Number(light.bulbradius);
  if (Number.isFinite(bulbradius)) {
    line += ` bulbradius="${formatMjcfNumber(Math.max(0, bulbradius))}"`;
  }
  if (typeof light.trackingMode === "string" && light.trackingMode.trim()) {
    line += ` mode="${xmlEscapeAttr(light.trackingMode.trim())}"`;
  }
  const target =
    typeof light.targetBodyUuid === "string" && bodyUuidToXmlName[String(light.targetBodyUuid)]
      ? bodyUuidToXmlName[String(light.targetBodyUuid)]
      : typeof light.targetBodyName === "string" && light.targetBodyName.trim()
        ? light.targetBodyName.trim()
        : "";
  if (target) line += ` target="${xmlEscapeAttr(target)}"`;

  const attrs =
    light.attrs && typeof light.attrs === "object"
      ? (light.attrs as Record<string, string>)
      : {};
  Object.entries(attrs)
    .filter(([key, value]) => {
      const normalized = String(key || "").trim().toLowerCase();
      return normalized && value != null && !RESERVED_LIGHT_ATTRS.has(normalized);
    })
    .sort(([keyA], [keyB]) => {
      const a = String(keyA).trim().toLowerCase();
      const b = String(keyB).trim().toLowerCase();
      const ai = LIGHT_ATTR_ORDER.indexOf(a);
      const bi = LIGHT_ATTR_ORDER.indexOf(b);
      if (ai !== -1 || bi !== -1) {
        return (
          (ai === -1 ? Number.MAX_SAFE_INTEGER : ai) -
          (bi === -1 ? Number.MAX_SAFE_INTEGER : bi)
        );
      }
      return a.localeCompare(b);
    })
    .forEach(([key, value]) => {
      line += ` ${key}="${xmlEscapeAttr(String(value))}"`;
    });
  return line;
};

export const emitMountedLightsXml = ({
  indent,
  mountedLights,
  lightExportNameByUuid,
  bodyUuidToXmlName,
  helpers,
}: {
  indent: string;
  mountedLights: ExportLight[];
  lightExportNameByUuid: Record<string, string>;
  bodyUuidToXmlName: Record<string, string>;
  helpers: LightXmlHelpers;
}): string => {
  const { arrayToString, findSiteByIdentifier, normalizeQuat, quatRotateVec, sanitizeMjcfName } =
    helpers;
  let xml = "";
  mountedLights.forEach((light) => {
    const exportName =
      (light?.uuid && lightExportNameByUuid[String(light.uuid)]) ||
      sanitizeMjcfName(light?.name || light?.uuid || "light");
    let localPos = safeVec3(light.position, [0, 0, 0]);
    let localDir = safeVec3(light.direction, [0, 0, -1]);
    if (localDir.every((value) => Math.abs(value) < 1e-8)) localDir = [0, 0, -1];
    if (light.mountType === "site" && light.mountSiteUuid) {
      const site = findSiteByIdentifier(String(light.mountSiteUuid));
      if (site) {
        const sitePos = safeVec3(site.position, [0, 0, 0]);
        const siteQuat = normalizeQuat(site.orientation);
        const rotatedPos = quatRotateVec(siteQuat, localPos);
        localPos = [
          Number(sitePos[0]) + Number(rotatedPos[0]),
          Number(sitePos[1]) + Number(rotatedPos[1]),
          Number(sitePos[2]) + Number(rotatedPos[2]),
        ];
        localDir = quatRotateVec(siteQuat, localDir);
      } else {
        logger.warn(
          `[xmlGenerationActions] Light "${exportName}" references missing mount site "${String(light.mountSiteUuid)}"; exporting body-local transform.`
        );
      }
    }
    let line = `${indent}  <light name="${exportName}" pos="${arrayToString(localPos)}" dir="${arrayToString(localDir)}"`;
    line = appendLightAttrs({ line, light, bodyUuidToXmlName, helpers });
    xml += `${line}/>\n`;
  });
  return xml;
};

export const emitFixedLightsXml = ({
  lightValuesForExport,
  lightExportNameByUuid,
  bodyUuidToXmlName,
  helpers,
}: {
  lightValuesForExport: ExportLight[];
  lightExportNameByUuid: Record<string, string>;
  bodyUuidToXmlName: Record<string, string>;
  helpers: LightXmlHelpers;
}): string => {
  const { arrayToString, sanitizeMjcfName, viewerVecToMujoco } = helpers;
  let xml = "";
  lightValuesForExport.forEach((light) => {
    if (!light || light.kind !== "fixed") return;
    const exportName =
      (light?.uuid && lightExportNameByUuid[String(light.uuid)]) ||
      sanitizeMjcfName(light?.name || light?.uuid || "light");
    const pos = viewerVecToMujoco(safeVec3(light.position, [0, 0, 0]));
    const dir = viewerVecToMujoco(safeVec3(light.direction, [0, 0, -1]));
    let line = `    <light name="${exportName}" pos="${arrayToString(pos)}" dir="${arrayToString(dir)}"`;
    line = appendLightAttrs({ line, light, bodyUuidToXmlName, helpers });
    xml += `${line}/>\n`;
  });
  return xml;
};
